import { useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import PropTypes from 'prop-types';

import FormControl from '@zenbusiness/zui/FormControl';
import ZuiTextField from '@zenbusiness/zui/TextField';

import * as Styled from './TagInputField.style';

/**
 * Tag input field - allows users to add multiple tags/items
 * @param {object} props - Component props
 * @param {object} props.def - Field definition
 * @param {Array<string>} props.value - Current array of tags
 * @param {Function} props.onChange - Change handler
 * @param {boolean} [props.disabled=false] - Whether field is disabled
 * @param {string} [props.error] - Error message to display
 */
function TagInputField({ def, value = [], onChange, disabled = false, error }) {
  const [inputValue, setInputValue] = useState('');

  const tags = Array.isArray(value) ? value : [];
  const { tagConfig = {} } = def;
  const { maxTags = 10, addButtonText = 'Add' } = tagConfig;

  const canAddMore = tags.length < maxTags;

  const handleInputChange = (event) => {
    setInputValue(event.target.value);
  };

  const handleAddTag = () => {
    const trimmedValue = inputValue.trim();

    if (!trimmedValue) return;
    if (!canAddMore) return;
    if (tags.includes(trimmedValue)) return; // Prevent duplicates

    onChange([...tags, trimmedValue]);
    setInputValue('');
  };

  const handleRemoveTag = (tagToRemove) => {
    onChange(tags.filter((tag) => tag !== tagToRemove));
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddTag();
    }
  };

  const fieldId = `tag-input-${def.id}`;
  const hasError = Boolean(error);

  return (
    <FormControl
      component="fieldset"
      disabled={disabled}
      error={hasError}
      fullWidth
      required={def.required}
    >
      {def.label && (
        <Styled.FieldLabel
          component="legend"
          id={`${fieldId}-label`}
          required={def.required}
          size="xl"
          weight="bold"
        >
          {def.label}
        </Styled.FieldLabel>
      )}

      {def.description && <Styled.FieldDescription>{def.description}</Styled.FieldDescription>}

      <Styled.InputContainer>
        <ZuiTextField
          disabled={disabled || !canAddMore}
          fullWidth
          id={fieldId}
          placeholder={def.placeholder || tagConfig.placeholder || 'Enter value'}
          value={inputValue}
          variant="outlined"
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
        />
        <Styled.AddButton
          disabled={disabled || !inputValue.trim() || !canAddMore}
          startIcon={<AddIcon />}
          onClick={handleAddTag}
        >
          {addButtonText}
        </Styled.AddButton>
      </Styled.InputContainer>

      {tags.length > 0 && (
        <Styled.TagsContainer>
          <Styled.TagsList>
            {tags.map((tag) => (
              <Styled.Tag key={tag}>
                <Styled.TagText>{tag}</Styled.TagText>
                <Styled.TagRemoveButton
                  aria-label={`Remove ${tag}`}
                  disabled={disabled}
                  type="button"
                  onClick={() => handleRemoveTag(tag)}
                >
                  <Styled.RemoveIcon />
                </Styled.TagRemoveButton>
              </Styled.Tag>
            ))}
          </Styled.TagsList>
        </Styled.TagsContainer>
      )}

      {(error || def.helpText) && (
        <Styled.FieldHelperText error={hasError}>{error || def.helpText}</Styled.FieldHelperText>
      )}
    </FormControl>
  );
}

TagInputField.propTypes = {
  def: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['tag_input']).isRequired,
    label: PropTypes.string,
    description: PropTypes.string,
    helpText: PropTypes.string,
    required: PropTypes.bool,
    placeholder: PropTypes.string,
    tagConfig: PropTypes.shape({
      maxTags: PropTypes.number,
      placeholder: PropTypes.string,
      addButtonText: PropTypes.string
    })
  }).isRequired,
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  error: PropTypes.string
};

export default TagInputField;
